import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { createDialogConfig } from "@utils";
import { BehaviorSubject } from "rxjs";
import { ErrorDescriptor } from "utils/error-utils";

export interface AlertData {
  title?: string;
  message?: string;
  acceptOnly?: boolean;
  acceptButtonText?: string;
  closeButtonText?: string;
  reverseButtonOrder?: boolean;

  /** Whether the user can use escape or clicking on the backdrop to close the modal. */
  disableClose?: boolean;

  /**
   * When provided, clicking the accept button sets this subject to `true` instead of closing the dialog.
   * The caller can subscribe to react, and set it back to `false` to stop loading.
   * The caller is responsible for closing the dialog via `dialogRef.close()`.
   */
  isLoading$?: BehaviorSubject<boolean>;

  /**
   * When provided, clicking the accept button navigates to this URL (via `location.href`) after closing the dialog.
   */
  acceptUrl?: string;
}

export type AlertResult = boolean;

@Component({
  selector: "app-alert",
  templateUrl: "./alert.component.html",
  styleUrls: ["./alert.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AlertComponent implements OnInit {
  public title?: string;
  public message?: string;
  public acceptOnly: boolean = true;
  public acceptButtonText = "Ok";
  public closeButtonText = "Close";
  public reverseButtonOrder: boolean = false;

  public isLoading$?: BehaviorSubject<boolean>;
  private isLoadingSupported = false;
  public acceptUrl?: string;

  public constructor(
    @Inject(MAT_DIALOG_DATA) private dialogData: AlertData,
    private dialogRef: MatDialogRef<AlertComponent, AlertResult>
  ) {}

  /**
   * Show a modal dialog with an error message.
   */
  public static openErrorDialogFromErrorDescriptor(
    matDialog: MatDialog,
    descriptor?: ErrorDescriptor | null
  ): MatDialogRef<AlertComponent, AlertResult> {
    const config = createDialogConfig<AlertData>({
      title: "Error",
      message: "An unexpected error occurred. Please try again.",
      acceptButtonText: "Close",
    });

    if (descriptor) {
      config.data.title = descriptor.title;
      config.data.message = descriptor.message;
    }

    config.autoFocus = "dialog";
    return matDialog.open<AlertComponent, AlertData, AlertResult>(
      AlertComponent,
      config
    );
  }

  /**
   * Show a modal dialog with an error message.
   */
  public static openErrorDialog(
    matDialog: MatDialog,
    message: string | null = null
  ): MatDialogRef<AlertComponent, AlertResult> {
    const config = createDialogConfig<AlertData>({
      title: "Error",
      message: message ?? "An unexpected error occurred. Please try again.",
      acceptButtonText: "Close",
    });
    config.autoFocus = "dialog";
    return matDialog.open<AlertComponent, AlertData, AlertResult>(
      AlertComponent,
      config
    );
  }

  public static openDialog(
    matDialog: MatDialog,
    data: AlertData
  ): MatDialogRef<AlertComponent, AlertResult> {
    const config = createDialogConfig<AlertData>(data, {
      disableClose: data.disableClose,
    });
    return matDialog.open<AlertComponent, AlertData, AlertResult>(
      AlertComponent,
      config
    );
  }

  public ngOnInit() {
    const {
      title,
      message,
      acceptOnly,
      acceptButtonText,
      closeButtonText,
      reverseButtonOrder,
      isLoading$,
    } = this.dialogData;
    this.title = title;
    this.message = message;
    this.acceptOnly = acceptOnly ?? this.acceptOnly;
    this.acceptButtonText = acceptButtonText ?? this.acceptButtonText;
    this.closeButtonText = closeButtonText ?? this.closeButtonText;
    this.reverseButtonOrder = reverseButtonOrder ?? this.reverseButtonOrder;
    this.isLoadingSupported = !!isLoading$;
    this.isLoading$ = isLoading$ ?? new BehaviorSubject<boolean>(false);
    this.acceptUrl = this.dialogData.acceptUrl;
  }

  public handleAccept() {
    if (this.isLoadingSupported) {
      this.isLoading$.next(true);
    } else {
      this.dialogRef.close(true);
    }
  }
}
